import React from 'react';

/** Screen-reader text appended to links that open a new tab. */
export const NEW_TAB_TEXT = '(opens in a new tab)';

/**
 * `rel` for a link: with target="_blank" it always carries "noopener noreferrer"
 * (merged with any rel the caller passed); otherwise the caller's rel is kept as is.
 */
export function newTabRel(target, rel) {
  if (target !== '_blank') return rel;
  const parts = new Set(String(rel || '').split(/\s+/).filter(Boolean));
  parts.add('noopener');
  parts.add('noreferrer');
  return [...parts].join(' ');
}

/** The sr-only "(opens in a new tab)" hint, only when target is "_blank". */
export function NewTabHint({ target, label = NEW_TAB_TEXT }) {
  if (target !== '_blank') return null;
  return <span className="sr-only"> {label}</span>;
}

/** The arrow for same-site navigation and in-page actions. */
export const ARROW_INTERNAL = '→';
/** The arrow for things that open outside the site (another origin, email, maps). */
export const ARROW_EXTERNAL = '↗';

// A scheme at the start of an href ("mailto:", "https:", "tel:"): letters, digits, + . -.
const SCHEME_RE = /^([a-z][a-z0-9+.-]*):/i;
// Hand-offs to the phone: they leave the page but open no site, so they carry no arrow.
const NO_ARROW_SCHEMES = new Set(['tel', 'sms']);

/**
 * Where an action goes (upgrade brief §2.1, arrow semantics):
 * - 'internal': a router `to`; an `href` on this site (a path, `#fragment`, `?query`, a
 *   relative link, or an absolute URL on the page's own origin); neither (a `<button>`: an
 *   in-page action, e.g. opening a dialog)
 * - 'external': an `href` to another origin, `mailto:`, or any other scheme (maps apps …)
 * - 'call': `tel:` / `sms:` (hands off to the phone: no arrow)
 */
export function linkDestination({ to, href } = {}) {
  if (to !== undefined && to !== null) return 'internal';
  if (href === undefined || href === null) return 'internal';
  const h = String(href).trim();
  if (!h || h.startsWith('#') || h.startsWith('?')) return 'internal';
  if (h.startsWith('/') && !h.startsWith('//')) return 'internal';
  const scheme = SCHEME_RE.exec(h);
  if (scheme) {
    const name = scheme[1].toLowerCase();
    if (NO_ARROW_SCHEMES.has(name)) return 'call';
    if (name !== 'http' && name !== 'https') return 'external';
  } else if (!h.startsWith('//')) {
    return 'internal'; // a relative path ("pricing", "./faq")
  }
  try {
    const here = typeof window !== 'undefined' && window.location ? window.location.href : undefined;
    const url = new URL(h, here);
    return here && url.origin === new URL(here).origin ? 'internal' : 'external';
  } catch (e) {
    return 'external';
  }
}

/**
 * The glyph of an action's arrow: `arrow` false / null → none; a string or node → that
 * (an explicit override, e.g. '↺' retry or '↓' in-page jump); true (the default) → by
 * destination: '→' same site (router `to`, a same-origin `href`, a `<button>`), '↗' outside
 * the site (another origin, `mailto:`, maps), none for `tel:` / `sms:`.
 */
export function arrowGlyph(arrow, { to, href } = {}) {
  if (arrow === false || arrow === null || arrow === undefined) return null;
  if (arrow !== true) return arrow;
  const kind = linkDestination({ to, href });
  if (kind === 'call') return null;
  return kind === 'external' ? ARROW_EXTERNAL : ARROW_INTERNAL;
}
